import type { PaginationModel, SortModel } from '@/components'
import type { Filters } from '@/types/filter.types.ts'
import { apiClient, handleResponse } from '@/utils/axios-client.ts'
import { buildSearchParameters } from '@/utils/search-paramater-url.ts'
import { z } from 'zod'
import {
  CreateVehicleFormSchema,
  EfficiencyHistorySchema,
  OdometerHistorySchema,
  StateOfChargeHistorySchema,
  VehiclePageSchema,
  VehicleSchema,
  type CreateVehicleForm,
  type EfficiencyHistory,
  type OdometerHistory,
  type StateOfChargeHistory,
  type Vehicle,
  type VehiclePage
} from './vehicle.data'

const ENDPOINT = '/api/v1/vehicles'

// ============================================================================
// Vehicle List Operations
// ============================================================================

/**
 * Fetch a paginated list of vehicles for a company
 * Used by: vehicle-management list view, fleet-dashboard
 */
export const fetchVehicleList = async (
  filters?: Filters,
  sort?: SortModel<Vehicle>,
  pagination?: PaginationModel,
  signal?: AbortSignal
): Promise<VehiclePage> => {
  const searchParams = buildSearchParameters<Vehicle>(filters || {}, {
    sorting: sort,
    pagination: pagination
  })

  return await handleResponse(
    () => apiClient.get(ENDPOINT, { params: searchParams, signal }),
    VehiclePageSchema
  )
}

// ============================================================================
// Vehicle Details Operations
// ============================================================================

/**
 * Fetch detailed information for a specific vehicle
 * Used by: vehicle details page
 */
export const fetchVehicleDetails = async (
  vin: string,
  signal?: AbortSignal
): Promise<Vehicle> => {
  return await handleResponse(
    () => apiClient.get(`${ENDPOINT}/${encodeURIComponent(vin)}`, { signal }),
    VehicleSchema
  )
}

/** Look-back window for a history series. Either a `range` preset, or an explicit
 * `from`/`to` window (drag-zoom); all fields omitted requests the full history. */
export interface HistoryWindow {
  /** ISO-8601 Period look-back (e.g. 'P7D'). */
  range?: string
  /** Start of an explicit window, paired with `to`. */
  from?: Date
  /** End of an explicit window. */
  to?: Date
}

/**
 * Fetch a bucketed history series for a vehicle (odometer, state-of-charge, …).
 * @param vin Vehicle identification number
 * @param segment History endpoint segment, e.g. 'odometer'
 * @param schema Zod schema validating the response
 * @param window Look-back window; an empty object requests the full history.
 * @param signal Optional abort signal
 */
const fetchVehicleHistory = async <T>(
  vin: string,
  segment: string,
  schema: z.ZodType<T>,
  window: HistoryWindow = {},
  signal?: AbortSignal
): Promise<T> => {
  const { range, from, to } = window
  return await handleResponse(
    () =>
      apiClient.get(`${ENDPOINT}/${encodeURIComponent(vin)}/history/${segment}`, {
        params: {
          ...(range ? { range } : {}),
          ...(from ? { from: from.toISOString() } : {}),
          ...(to ? { to: to.toISOString() } : {})
        },
        signal
      }),
    schema
  )
}

/** Historic odometer increases, bucketed over time. */
export const fetchOdometerHistory = (
  vin: string,
  window?: HistoryWindow,
  signal?: AbortSignal
): Promise<OdometerHistory> =>
  fetchVehicleHistory(vin, 'odometer', OdometerHistorySchema, window, signal)

/** Historic state-of-charge (avg/min/max), bucketed over time. */
export const fetchStateOfChargeHistory = (
  vin: string,
  window?: HistoryWindow,
  signal?: AbortSignal
): Promise<StateOfChargeHistory> =>
  fetchVehicleHistory(vin, 'state-of-charge', StateOfChargeHistorySchema, window, signal)

/** Historic energy efficiency, bucketed over time. */
export const fetchEfficiencyHistory = (
  vin: string,
  window?: HistoryWindow,
  signal?: AbortSignal
): Promise<EfficiencyHistory> =>
  fetchVehicleHistory(vin, 'efficiency', EfficiencyHistorySchema, window, signal)

// ============================================================================
// Vehicle Creation Operations
// ============================================================================

function toApiPayload(formData: CreateVehicleForm) {
  const { contract, ...rest } = formData
  return {
    ...rest,
    contract: contract.enabled
      ? {
          startDate: contract.startDate,
          endDate: contract.endDate,
          contractedMileage: contract.contractedMileage,
          startDateOdometer: contract.startDateOdometer
        }
      : null
  }
}

/**
 * Create a new vehicle in a fleet/site
 * Used by: vehicle creation form
 */
export const createVehicle = async (vehicleData: CreateVehicleForm): Promise<void> => {
  const validationResult = CreateVehicleFormSchema.safeParse(vehicleData)

  if (!validationResult.success) {
    throw new Error(`Invalid vehicle data: ${z.prettifyError(validationResult.error)}`)
  }

  return handleResponse(() => apiClient.post(ENDPOINT, toApiPayload(validationResult.data)), z.any())
}

// ============================================================================
// Vehicle Update Operations
// ============================================================================
export const updateVehicle = async (
  vin: string,
  vehicleData: Partial<CreateVehicleForm>
): Promise<Vehicle> => {
  const validationResult = CreateVehicleFormSchema.safeParse(vehicleData)

  if (!validationResult.success) {
    throw new Error(`Invalid vehicle data: ${z.prettifyError(validationResult.error)}`)
  }
  return handleResponse(
    () => apiClient.put(`${ENDPOINT}/${encodeURIComponent(vin)}`, toApiPayload(validationResult.data)),
    VehicleSchema
  )
}

// ============================================================================
// Vehicle Delete Operations
// ============================================================================
export const deleteVehicle = async (vin: string): Promise<Vehicle> => {
  return handleResponse(
    () => apiClient.delete(`${ENDPOINT}/${encodeURIComponent(vin)}`),
    z.any()
  )
}
